import React, { useEffect, useState } from 'react';
import { MainHeader } from '@/components/marketplace/MainHeader';
import { MarketplacePropertiesLayout } from '@/components/marketplace/MarketplacePropertiesLayout';
import { MarketplaceFooter } from '@/components/marketplace/MarketplaceFooter';
import { PreviewListener } from '@/components/marketplace/PreviewListener';
import { WhatsAppFloatingButton } from '@/components/properties/WhatsAppFloatingButton';
import { marketplaceSupabase } from '@/integrations/supabase/marketplaceClient';
import { useTenant } from '@/contexts/TenantContext';
import { useHomePageBlocks } from '@/hooks/useSitePages';
import { BlockRenderer } from '@/components/page-builder/BlockRenderer';
import { useTemplate } from '@/contexts/TemplateContext';

export const RealtorClassicTemplate: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { tenantId, tenant, loading: isTenantLoading } = useTenant();
  const [profileData, setProfileData] = useState<{
    displayName: string;
    avatarUrl?: string;
    whatsapp?: string;
  }>({    displayName: 'Corretor' 
  });
  const { data: homeBlocks = [], isLoading: isBlocksLoading } = useHomePageBlocks(tenantId);

  useEffect(() => {
    const loadProfile = async () => {
      if (!tenantId) return;

      try {
        const { data: realtorData } = await marketplaceSupabase
          .from('realtors')
          .select('*')
          .eq('tenant_id', tenantId)
          .eq('is_active', true)
          .order('is_primary', { ascending: false })
          .limit(1)
          .maybeSingle();

        const { data: tenantData } = await marketplaceSupabase
          .from('tenants')
          .select('name, logo_url')
          .eq('id', tenantId)
          .maybeSingle();

        setProfileData({
          displayName: realtorData?.name || tenantData?.name || 'Corretor',
          avatarUrl: realtorData?.photo_url || tenantData?.logo_url || undefined,
          whatsapp: realtorData?.whatsapp || realtorData?.phone,
        });

      } catch (err) {
        console.error('[RealtorClassicTemplate] Error loading profile:', err);
      }
    };

    loadProfile();
  }, [tenantId]);

  const { currentTemplate } = useTemplate();

  if (isTenantLoading || isBlocksLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[var(--brand-primary)] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  const siteTemplate = currentTemplate;
  
  let transparentVariant: 'none' | 'light' | 'pill' = 'light';
  if (siteTemplate === 'modern_search') transparentVariant = 'pill';

  return (
    <div className="min-h-screen bg-background flex flex-col animate-in fade-in duration-500">
      <PreviewListener />
      
      {siteTemplate !== 'profile' && (
        <MainHeader 
          avatarUrl={profileData.avatarUrl} 
          displayName={profileData.displayName} 
          transparentVariant={transparentVariant}
        />
      )}
      
      <div className={`flex-1 ${transparentVariant === 'none' ? 'pt-24 lg:pt-28' : ''}`}>
        <BlockRenderer blocks={homeBlocks} />
        {/* Always render properties below the blocks for now, until it becomes its own block type */}
        <MarketplacePropertiesLayout showHeader={false} heroVariant={siteTemplate} />
      </div>

      {children}
      
      <MarketplaceFooter />
      
      {profileData.whatsapp && (
        <WhatsAppFloatingButton 
          phoneNumber={profileData.whatsapp} 
          message="Olá! Estou navegando no seu site e gostaria de mais informações." 
        />
      )}
    </div>
  );
};
